import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { parseContentDispositionFilename } from '../core/api-response-utils';
import {
  Pageable,
  ListChargeFilterSpec,
  SearchChargeFilterSpec,
  WrapperResponse
} from './service-common';
import type {
  CollectionAttemptEventDTO,
  WrapperPageableResponseChargeDTO,
  WrapperResponseChargeDTO,
  WrapperResponseListPaymentAttemptDTO
} from '../core/models';

@Injectable({
  providedIn: 'root'
})
export class ChargeService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  /**
   * Envía el correo de la liga de pago (POST /charges/{chargeId}/recall).
   * En recibos MANUAL el API crea la liga y cambia el método a LINK.
   */
  recall(chargeId: number, email: string): Observable<WrapperResponseChargeDTO> {
    return this.http.post<WrapperResponseChargeDTO>(`${this.apiUrl}/charges/${chargeId}/recall`, { email });
  }

  /**
   * Aplica un pago manual al cargo (POST /charges/{chargeId}/pay).
   */
  applyPayment(
    chargeId: number,
    body: {
      paymentFormId: number;
      reference: string;
      bank?: string;
      description?: string;
      paymentDate: string;
      sendReceiptEmail?: boolean;
    }
  ): Observable<WrapperResponseChargeDTO> {
    return this.http.post<WrapperResponseChargeDTO>(`${this.apiUrl}/charges/${chargeId}/pay`, body);
  }

  /**
   * Actualiza un pago manual ya registrado (PUT /charges/{chargeId}/pay).
   * Requiere cobro MANUAL y venta no facturada.
   */
  updatePayment(
    chargeId: number,
    body: {
      paymentFormId: number;
      reference: string;
      bank?: string;
      description?: string;
      paymentDate: string;
      sendReceiptEmail?: boolean;
    }
  ): Observable<WrapperResponseChargeDTO> {
    return this.http.put<WrapperResponseChargeDTO>(`${this.apiUrl}/charges/${chargeId}/pay`, body);
  }

  /**
   * Cancela un pago aplicado (POST /charges/{chargeId}/pay/cancel).
   */
  cancelPayment(
    chargeId: number,
    body: { cancelReason?: string } = {}
  ): Observable<WrapperResponseChargeDTO> {
    return this.http.post<WrapperResponseChargeDTO>(
      `${this.apiUrl}/charges/${chargeId}/pay/cancel`,
      body
    );
  }

  /**
   * Reintento manual de cobro recurrente (POST /charges/{chargeId}/retry).
   */
  retryRecurringCharge(chargeId: number): Observable<WrapperResponseChargeDTO> {
    return this.http.post<WrapperResponseChargeDTO>(`${this.apiUrl}/charges/${chargeId}/retry`, {});
  }

  /**
   * Actualiza la fecha compromiso de pago del CxC (PATCH /charges/{chargeId}/cxc/commitment-date).
   * Enviar `null` limpia el valor.
   */
  updateCommitmentDate(
    chargeId: number,
    commitmentDate: string | null
  ): Observable<WrapperResponseChargeDTO> {
    return this.http.patch<WrapperResponseChargeDTO>(
      `${this.apiUrl}/charges/${chargeId}/cxc/commitment-date`,
      { commitmentDate }
    );
  }

  /**
   * Lista intentos de pago de un cargo (GET /charges/{chargeId}/attempts).
   */
  listPaymentAttempts(chargeId: number): Observable<WrapperResponseListPaymentAttemptDTO> {
    return this.http.get<WrapperResponseListPaymentAttemptDTO>(
      `${this.apiUrl}/charges/${chargeId}/attempts`
    );
  }

  /**
   * Historial de un intento de pago (GET /charges/{chargeId}/attempts/{attemptId}/events).
   */
  listAttemptEvents(chargeId: number, attemptId: number): Observable<WrapperResponse<CollectionAttemptEventDTO[]>> {
    return this.http.get<WrapperResponse<CollectionAttemptEventDTO[]>>(
      `${this.apiUrl}/charges/${chargeId}/attempts/${attemptId}/events`
    );
  }

  /**
   * Lista cargos con filtros y paginación (GET /charges/list).
   * Útil para reportes: dueDate[gte], dueDate[lte], status[in], createdAt[gte], createdAt[lte], etc.
   */
  listCharges(
    spec: ListChargeFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperPageableResponseChargeDTO> {
    let params = this.buildSpecParams(spec);
    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));

    if (pageable.sort && pageable.sort.length > 0) {
      pageable.sort.forEach((sort) => {
        params = params.append('sort', sort);
      });
    }

    return this.http.get<WrapperPageableResponseChargeDTO>(`${this.apiUrl}/charges/list`, { params });
  }

  /**
   * Búsqueda de cargos (GET /charges) con SearchChargeFilterSpec.
   * Soporta filtros between (ej. dueDate[bw]) pasando un array [desde, hasta];
   * cada elemento se envía como query param con la misma clave.
   */
  searchCharges(
    spec: SearchChargeFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperPageableResponseChargeDTO> {
    let params = this.buildSpecParams(spec);
    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));

    if (pageable.sort && pageable.sort.length > 0) {
      pageable.sort.forEach((sort) => {
        params = params.append('sort', sort);
      });
    }

    return this.http.get<WrapperPageableResponseChargeDTO>(`${this.apiUrl}/charges`, { params });
  }

  /**
   * Exporta cargos filtrados a CSV (GET /charges/export).
   */
  exportChargesCsv(
    spec: SearchChargeFilterSpec = {},
    sort: string[] = ['dueDate,asc']
  ): Observable<{ blob: Blob; filename: string }> {
    let params = this.buildSpecParams(spec);
    sort.forEach((s) => {
      params = params.append('sort', s);
    });
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone) {
      params = params.append('timeZone', timeZone);
    }

    return this.http
      .get(`${this.apiUrl}/charges/export`, {
        params,
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map((resp) => ({
          blob: resp.body as Blob,
          filename:
            parseContentDispositionFilename(resp.headers.get('Content-Disposition')) ?? 'cobranza.csv'
        }))
      );
  }

  private buildSpecParams(spec: ListChargeFilterSpec | SearchChargeFilterSpec): HttpParams {
    let params = new HttpParams();
    Object.entries(spec).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== undefined && v !== null && v !== '') {
            params = params.append(key, String(v));
          }
        });
      } else if (value !== '') {
        params = params.append(key, String(value));
      }
    });
    return params;
  }
}
